import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarPlus, Check, ChevronDown, Copy } from 'lucide-react'
import AccountGroup from './AccountGroup'
import {
  fetchCalendarLinks,
  readerSummary,
  replaceCalendarLinks,
  type CalendarLink,
} from '../api/calendarLinks'

/**
 * Subscribing to your own calendar (D-533).
 *
 * **A link per child, not one per family.** `asked` — *"def one per child so
 * they can color code"*: a calendar client colours a whole subscription, so a
 * shared feed gives three children one colour, and picking one dancer out of a
 * shared week is exactly what a colour is for. So this is a row per dancer,
 * each with its own address, plus one for her teaching if she teaches.
 *
 * **The URL is copied and never rendered.** A calendar client cannot sign in, so
 * the address *is* the credential — which makes it the one string on this
 * screen that must not appear in a screenshot or be read out over the phone.
 * The one exception is the fallback below, where a browser has refused to copy
 * and showing it is the only remaining way to hand it over.
 *
 * **Deliberately counter-intuitive, and worth saying once**: this exists to get
 * a family to open the app less. The studio's competitor is not another app, it
 * is a parent forgetting — and a class time inside the calendar she already
 * checks beats one behind a login she has to remember.
 */
export default function CalendarLinksPanel({
  isKiosk,
  grouped = false,
}: {
  isKiosk: boolean
  /**
   * Draw it as one of the account page's closed groups (D-719) rather than as an
   * open block below them.
   *
   * `asked`, 2026-09-17: *"lets make the 'Add to your Calendar', the family member
   * lines, and the 'how do i add this to my calendar' stuff to its own panel and
   * have it auto collapsed when navigating to the account page too"*. It was the
   * one thing on that page always open, and on a phone it was most of the scroll.
   *
   * **The group is drawn here rather than around this component**, so a reader
   * with nothing to subscribe to still gets nothing at all: the guards above
   * return null before any group exists, and a closed group that opens onto
   * nothing is worse than no group.
   */
  grouped?: boolean
}) {
  const queryClient = useQueryClient()

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['calendar-links'],
    queryFn: fetchCalendarLinks,

    /*
     * **Nothing to offer a wall tablet.** The kiosk boundary refuses this route
     * already (D-233), so asking would paint a red error on a screen in the
     * lobby — and minting a family's calendar credential from a shared device
     * is the thing that boundary exists to stop.
     */
    enabled: !isKiosk,
  })

  const [replacing, setReplacing] = useState(false)

  const replace = useMutation({
    mutationFn: replaceCalendarLinks,
    onSuccess: (fresh) => {
      setReplacing(false)

      // The response *is* the new state, and it holds URLs that exist nowhere
      // else — a refetch would be a second round trip for the same answer.
      queryClient.setQueryData(['calendar-links'], fresh)
    },
  })

  /*
   * Nothing to offer an account with no dancers and no teaching, and an empty
   * panel explaining that would be a paragraph about a feature she cannot use.
   *
   * **After the error branch**, or a failed load renders as "you have no
   * calendar" — every database-backed endpoint refuses for ~75 seconds after a
   * deploy (D-33), so that would be the ordinary experience rather than an edge
   * case (D-209).
   */
  if (!isPending && !isError && (data?.length ?? 0) === 0) return null

  /*
   * <b>Below the hooks, never above them</b> (D-759, found by the oxlint bump).
   *
   * This stood between <c>useQuery</c> and <c>useState</c>, so the component
   * called four hooks for a signed-in reader and two on a wall tablet. React
   * requires the same hooks in the same order on every render of a mounted
   * component, and the two states are not mutually exclusive here: a teacher's
   * own phone is a classroom device, and the PIN cookie and the session cookie
   * are separate and both live (D-128, D-236) - so `isKiosk` can change under a
   * component that is already on screen, and the render after it throws rather
   * than hiding a panel. <b>Which error depends on the direction</b>, and the
   * one that matters here is the signing-in one: kiosk to person is
   * <c>Rendered more hooks than during the previous render</c> (`observed`
   * against the old order); person to kiosk is <i>rendered fewer hooks than
   * expected</i>. Both are a white screen.
   *
   * The query is still the thing that matters for the boundary: it carries
   * `enabled: !isKiosk`, so a wall tablet never asks for a family's calendar
   * credentials (D-233). What moved is only where we stop drawing.
   */
  if (isKiosk) return null

  const links = data ?? []

  const summary =
    links.length === 0
      ? undefined
      : `${links.length} ${links.length === 1 ? 'link' : 'links'} · Google, Apple, Outlook`

  const inside = (
    <>
      {/* The group already carries this as its title. */}
      {!grouped && (
        <h2 className="mb-1.5 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Add to your calendar
        </h2>
      )}

      <p className="mb-3 text-[13px] text-ink-3">
        Put class times straight into the calendar you already use &mdash; Google, Apple, Outlook.
        There is one link per dancer, so you can give each of them their own colour.
      </p>



      {isPending && <p className="text-[13px] text-ink-3">Loading...</p>}

      {isError && (
        <p role="alert" className="text-[13px] text-crit">
          {(error as Error).message}
        </p>
      )}

      {replace.error && (
        <p role="alert" className="mb-2 text-[13px] text-crit">
          {(replace.error as Error).message}
        </p>
      )}

      {data && data.length > 0 && (
        <div className="rounded-2xl border border-line bg-surface px-4">
          {/*
            **Two is where it stops being a list and starts being a wall.**
            Measured against the real studio: 430 families carry one dancer,
            116 carry two, 23 carry three or four. Folding always would cost
            the 546 a tap to spare the 23; folding never leaves those 23 with
            nine or twelve buttons (D-564).
          */}
          {data.map((link) => (
            <Row key={link.dancerId ?? 'teaching'} link={link} folded={data.length > 2} />
          ))}
        </div>
      )}

      {data && data.length > 0 && <HowToAdd />}

      {data && data.length > 0 && (
        <div className="mt-3">
          {replacing ? (
            <div className="rounded-2xl border border-line bg-surface p-4">
              <p className="text-[13px]">
                This replaces {data.length === 1 ? 'your link' : `all ${data.length} of your links`}.
                Anywhere you have already added {data.length === 1 ? 'it' : 'them'} will stop
                updating until you add the new {data.length === 1 ? 'one' : 'ones'}.
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={replace.isPending}
                  onClick={() => replace.mutate()}
                  className="rounded-lg bg-crit px-3.5 py-2 text-[13px] font-semibold text-on-crit disabled:opacity-60"
                >
                  {replace.isPending ? 'Replacing...' : 'Yes, replace'}
                </button>

                <button
                  type="button"
                  onClick={() => setReplacing(false)}
                  className="rounded-lg border border-line px-3.5 py-2 text-[13px] font-semibold"
                >
                  Keep them
                </button>
              </div>
            </div>
          ) : (
            /*
             * **The only thing that ever stops a link**, since it does not
             * expire — `asked`: *"set it once and forget it"*. Which is why the
             * row above reports who has been collecting: that line is the whole
             * of the evidence somebody would act on.
             */
            /*
             * `py-1.5` for the hit area, not for the look. As a bare text link
             * it measured 115x20 on a phone — under the 24px the layout sweep
             * asks for, and it is the one control here somebody reaches for
             * while worried. Found by driving the screen; unreachable by
             * reading it (D-254).
             */
            <button
              type="button"
              onClick={() => setReplacing(true)}
              className="py-1.5 text-[13px] font-semibold text-ink-3 underline underline-offset-2"
            >
              Replace {data.length === 1 ? 'my link' : 'all my links'}
            </button>
          )}
        </div>
      )}
    </>
  )

  if (grouped) {
    return (
      <AccountGroup id="calendar" title="Add to your calendar" summary={summary}>
        {inside}
      </AccountGroup>
    )
  }

  return <section className="mt-8">{inside}</section>
}

/**
 * Google's own one-click subscribe address (D-561, amended).
 *
 * <b>Google cannot be handed a `webcal:` tap and it does not need a login of
 * ours.</b> `calendar.google.com/render?cid=…` opens Google Calendar with a
 * <i>subscribe to this calendar?</i> prompt.
 *
 * <b>On a computer.</b> An earlier version of this note said "in any browser,
 * on any platform - which removes the whole 'do it on a computer' paragraph
 * for Android", and that was exactly backwards (D-574). Google's own help:
 * *"To subscribe to a new calendar, you must use a computer web browser. You
 * can't subscribe to a calendar in the Google Calendar app for Android,
 * iPhone, or iPad."* On a phone the prompt still appears, the tap still says
 * yes, and nothing is ever added - which is why the copy now says so on the
 * button rather than in a fold.
 *
 * <b>Not an integration, deliberately.</b> Google's API will not subscribe
 * somebody to an external feed on their behalf - `calendarList.insert` refuses
 * an arbitrary ICS - so the only programmatic route would be writing events
 * into her calendar over OAuth: tokens to hold, consent to manage, revocation
 * to honour and a sync to reconcile for ever, to reproduce what a subscription
 * does for nothing.
 *
 * <b>`cid=` takes the `webcal:` address, raw</b> (D-575, reversing D-571).
 *
 * <b>D-571 changed this to an encoded `https:` address and was wrong on both
 * counts</b>, and it is worth writing out because the reasoning sounded
 * airtight: *Google's fetcher has no webcal scheme, so handing it one cannot
 * work.* True of the **fetch**, and irrelevant **here** - `cid=` is a handoff
 * that Google parses itself and converts internally, so the scheme in this
 * parameter is not the scheme Google fetches with. Two different URLs, one
 * name.
 *
 * The cost of that conflation was real: `cid=webcal://…` had always been
 * *accepted* - the calendar appeared under <i>Other calendars</i> - and after
 * D-571 Google refused the address outright with <i>"Unable to add calendar.
 * Check the URL."</i>, three times. So a working half was broken while the
 * broken half was being fixed.
 *
 * <b>What was actually stopping the sync was the HEAD 404</b> (D-573), which
 * is fixed and stays fixed. The two faults looked like one because correcting
 * the scheme is what made Google probe at all.
 *
 * <b>Raw, not encoded.</b> The documented form is the address appended whole;
 * encoded, Google is handed `webcal%3A%2F%2F…`, which is not a URL. D-571's
 * stated reason for encoding - that a future `?` would truncate it - is a real
 * hazard and is not worth breaking the working case for: this feed's address
 * is three fixed parts and a token, and if it ever gains a query string that
 * is the moment to think again.
 *
 * <b>Apple keeps `webcal:` and must.</b> That scheme is the handoff: it is
 * what makes the tap open Calendar.app rather than downloading a file, and
 * the fetch that follows is the same https request the feed already answers.
 */
const googleUrl = (webcal: string) =>
  `https://www.google.com/calendar/render?cid=${webcal}`

/**
 * Mid-sentence, a row's own name: <em>Add my teaching</em>.
 *
 * Only the teaching row is ever passed through here — a dancer's name is a
 * proper noun and keeps its capital. The point is that the words stay the
 * server's (D-564): the row is titled <q>My teaching</q> by
 * <code>CalendarFeedEndpoints</code>, and writing that phrase out a second
 * time is how a heading and the button under it end up disagreeing.
 */
const uncapitalised = (name: string) => name.charAt(0).toLowerCase() + name.slice(1)

/**
 * What to do with the address once it is copied.
 *
 * <b>Three sets of instructions for one link, which is the shape of the
 * problem rather than a failure of the design.</b> Google holds a subscription
 * on the *account*, so the Android app cannot add one at all and the web is
 * the only door; Apple takes the tap above; a computer is the same two minutes
 * on either. Written here so the person copying is told at the moment she has
 * it, rather than ringing the studio - and they are Tonya's words (A-16).
 *
 * <b>Closed by default.</b> Somebody who has done it once should not scroll
 * past a wall of instructions every time she opens her account.
 *
 * <b>Written here rather than in a content block, and that is a bound on
 * A-16</b> (`asked`, 2026-09-08: *"I don't think Tonya needs to be able to
 * edit those, its just how they work and we control that"*). A-16 exists so
 * the studio never needs a developer to change <i>its own</i> words; these are
 * Apple's and Google's menus. When Google moves one, Tonya is not the person
 * who will notice or reword it - we are. Same bound the build credit sits
 * under, and the same one `/admin/studio` already uses.
 */
function HowToAdd() {
  const [open, setOpen] = useState(false)

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center gap-1.5 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:text-ink"
      >
        <ChevronDown
          size={14}
          strokeWidth={2.5}
          className={open ? 'rotate-180 transition-transform' : 'transition-transform'}
          aria-hidden="true"
        />
        How do I add this to my calendar?
      </button>

      {open && (
        <div className="mt-1 flex flex-col gap-3 rounded-2xl border border-line bg-surface-2 px-4 py-3.5 text-[12.5px] leading-relaxed text-ink-2">
          <p>
            <strong className="font-semibold text-ink">iPhone, iPad or Mac</strong>
            <br />
            Tap Add to Apple Calendar and say yes when it asks. That is the whole of it &mdash;
            it stays up to date on its own from then on.
          </p>

          <p>
            <strong className="font-semibold text-ink">Google Calendar &mdash; on a computer</strong>
            <br />
            <strong className="font-semibold text-ink">
              This one cannot be done on a phone or a tablet.
            </strong>{' '}
            Google only lets you add a calendar by link in a web browser on a computer &mdash;
            Chrome on a PC or a Mac. Tapping the button on a phone opens the Google Calendar app,
            which will ask if you want to add it and then quietly do nothing.
          </p>

          <p>
            On the computer, either press Add to Google Calendar here, or use Copy link and then:
            open Google Calendar, find <strong className="font-semibold text-ink">Other calendars</strong>{' '}
            on the left, press the <strong className="font-semibold text-ink">+</strong> beside it,
            choose <strong className="font-semibold text-ink">From URL</strong>, paste the link and
            press <strong className="font-semibold text-ink">Add calendar</strong>.
          </p>

          <p>
            It is added to your Google <em>account</em>, not to that computer &mdash; so once it is
            in, it turns up on your phone and everywhere else you use that account. If it does not
            show on the phone straight away, open Google Calendar there, tap the menu, tap Settings
            and switch it on. Google fills the classes in over the next few minutes, so an empty
            calendar just after adding it is normal.
          </p>

          <p>
            <strong className="font-semibold text-ink">Anything else</strong>
            <br />
            Use Copy link, then paste it into your calendar&rsquo;s own subscribe box. In Outlook
            that is Add calendar, then Subscribe from web.
          </p>

          <p className="text-ink-3">
            Opening the link in a browser is not the same as subscribing &mdash; that copies the
            classes in once and they never update. Always paste it into the subscribe box.
          </p>
        </div>
      )}
    </div>
  )
}

function Row({ link, folded }: { link: CalendarLink; folded: boolean }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [open, setOpen] = useState(false)
  const firstDoor = useRef<HTMLAnchorElement>(null)

  /*
   * <b>The line under her name says the one thing that stops the commonest
   * mistake, until there is something truer to say</b> (D-577, `asked`:
   * *"maybe just use that spot where you had the 'nothing has' to say Google
   * only on Chrome/PC"*).
   *
   * <b>That slot was already there and was saying nothing useful.</b> It read
   * *"Nothing has collected this yet"* before she had done anything, which the
   * owner read as a fault rather than a state. It sits directly above the
   * three buttons, which makes it the closest words to the tap that go wrong -
   * closer than the banner this replaces, and closer than the fold nobody
   * opens.
   *
   * <b>It repeats per dancer, and here that is right.</b> D-576 moved this
   * message *out* of the row precisely to stop it repeating; the difference is
   * that it was a second line hanging under one button and breaking the row's
   * alignment. In the subtitle slot it costs no layout at all, and a warning
   * attached to a control is one that should appear wherever the control does.
   *
   * <b>It gives way the moment it is wrong.</b> Once something is fetching the
   * link she has plainly managed it, so the row switches to naming the reader -
   * which is also the evidence D-533 wanted, arriving exactly when it becomes
   * true. A caveat that outstays its usefulness is the next thing somebody
   * asks about.
   */
  const reader = readerSummary(link)
  const subtitle = reader || 'Google can only be added on a computer'

  /*
   * **The button she pressed is gone, so her focus has to land somewhere.**
   * Unfolding unmounts the control, which drops keyboard focus to the body —
   * she would then tab from the top of the page to reach the doors she just
   * asked for. Sending focus to the first one is the same move the browser
   * would make if this were a link.
   */
  useEffect(() => {
    if (open) firstDoor.current?.focus()
  }, [open])

  const copy = async () => {
    try {
      /*
       * `navigator.clipboard` is undefined outside a secure context and its
       * write can be refused outright. The old shape — fire and set "Copied" —
       * tells her it worked when nothing was on the clipboard, and she then
       * pastes whatever was there before into her calendar.
       */
      await navigator.clipboard.writeText(link.url)
      setState('copied')
    } catch {
      setState('failed')
    }
  }

  return (
    <div className="border-t border-line py-3 first:border-t-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/*
          A floor under the name, so three buttons wrap to their own line
          rather than squeezing "Sadie · read by Google Calendar" into a column
          three words wide. Seen on a 900px laptop; a flex child with only
          `flex-1` gives up its width to whatever sits beside it.
        */}
        <div className="min-w-[11rem] flex-1">
          <p className="text-[14px] font-semibold wrap-anywhere">{link.name}</p>
          {/*
            Rendered only when there is something to say (D-577). An empty
            paragraph would still occupy its line height and leave the name
            floating above a gap.
          */}
          {/* Not the faintest grey: this is the one hint on the panel that
              stops a wasted attempt, so it reads a step above a hint (D-577). */}
          <p className={reader ? "text-[12.5px] text-ink-3" : "text-[12.5px] font-medium text-ink-2"}>
            {subtitle}
          </p>
        </div>

        {/*
          **Three doors, and none of them is chosen for her** (`asked`,
          2026-09-08: *"have like a button for add to google, one for add to
          apple, and then one to just copy the url if people are adding it to
          a diff calendar than those"*). All three are always offered; from
          three rows up they wait behind one press rather than being shown at
          once, which is D-564 below and is about *how many at a time*, never
          about which device gets which.

          An earlier version showed the Apple one only to Apple-looking
          devices, which cost more than it saved: the owner could not find it
          on a Windows machine to check, and a button that appears on somebody
          else's phone and not on yours is harder to support than one that is
          simply named. Google needs only a browser and is the one route an
          Android phone has; `webcal:` is Apple's, and Windows usually hands
          it to Outlook; copying covers everything else.

          **Grouped, so they wrap as one.** Loose in the row they broke one to
          a line and one to the next, right-aligned against the name - three
          controls reading as two decisions. Whether they sit beside her name
          or under it, they stay together.

          The two subscribe doors are links rather than buttons because they
          are links: they leave the app, and the address rides in the href
          rather than in anything a screenshot would catch.
        */}
        {/*
          **Folded shut until asked, because three doors each is a wall**
          (D-564). Three controls is right for the 430 households with one
          dancer and reads as a wall of pink at four — twelve buttons, about
          500px of them on a phone. From three rows up the row offers one.

          The label names whose row it is rather than repeating *Add to
          calendar* four times, so the buttons are told apart by ear as well
          as by eye. It lower-cases the name the server sent rather than
          writing *my teaching* out again — the wording of that row is the
          API's (`CalendarFeedEndpoints`), and a second copy here would go on
          saying whatever it says today after the first one changed.
        */}
        {folded && !open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="ml-auto rounded-lg border border-accent px-3.5 py-2 text-[13px] font-semibold text-accent-2"
          >
            Add {link.dancerId ? link.name : uncapitalised(link.name)}
          </button>
        )}

        {/*
          Removed rather than hidden with a class: a `hidden` div is still in
          the document, and jsdom applies no CSS — so a test asserting the
          doors are shut would pass with the fold deleted.
        */}
        {(!folded || open) && (
          /*
            <b>The doors in a row, the caveat under the row</b> (D-574, D-576).
            A first cut hung it under the Google button alone, inside a column
            of its own - which took that button out of line with the two beside
            it and left a ragged tail under a tidy row. Reported on sight:
            <i>"this formatting looks bad with the on a computer only under the
            button."</i>

            <b>Under the group and naming Google, rather than beside it and
            pointing.</b> A note that has to sit under its own control to be
            understood is one that cannot be moved; naming the thing it is
            about frees it to live where the layout wants it, which is one line
            under three aligned buttons.
          */
          <div className="ml-auto flex flex-col items-end gap-1">
            <div className="flex flex-wrap items-center justify-end gap-2">
            {/*
              <b>The constraint stays out of the fold</b> (D-574). Google
              cannot subscribe to a calendar from the phone app at all - its
              own help says so in as many words - so on a phone this door is a
              dead end however it is worded, and the wording lived behind
              <i>How do I add this?</i>, which nobody opens. A parent taps
              first and reads afterwards.

              Said at every size rather than shown to phones only: no device
              sniffing, and it stays true on a laptop, where it is simply the
              instruction. Apple carries no such line, which is the honest
              difference - that one really does work on a phone.
            */}
            <a
              ref={firstDoor}
              href={googleUrl(link.webcalUrl)}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-accent px-3.5 py-2 text-[13px] font-semibold text-accent-2 no-underline"
            >
              Add to Google Calendar
            </a>

            <a
              href={link.webcalUrl}
              className="rounded-lg border border-accent px-3.5 py-2 text-[13px] font-semibold text-accent-2 no-underline"
            >
              Add to Apple Calendar
            </a>

            <button
              type="button"
              onClick={copy}
              className="flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent"
            >
              {state === 'copied' ? <Check size={13} strokeWidth={3} /> : <Copy size={13} strokeWidth={2} />}
              {state === 'copied' ? 'Copied' : 'Copy link'}
            </button>
            </div>
          </div>
        )}
      </div>

      {state === 'copied' && (
        /*
         * **Said at the moment of copying, because this is when the expectation
         * is set.** A calendar app refreshes on its own schedule and Google's
         * can be most of a day — so the first time a class moves and her phone
         * does not, the honest explanation has to already have been given, or
         * it reads as the studio getting it wrong.
         *
         * `role="status"` rather than `alert`: this is confirmation, not a
         * problem, and an assertive announcement would interrupt.
         */
        <p role="status" className="mt-2.5 flex gap-2 rounded-lg bg-surface-2 p-2.5 text-[12.5px] text-ink-3">
          <CalendarPlus size={14} strokeWidth={2} className="mt-px shrink-0" />
          <span>
            Paste this into your calendar app. Calendars refresh on their own schedule, so a change
            can take up to <strong className="font-semibold text-ink">24 hours</strong> to show up
            there. This app is always up to date.
          </span>
        </p>
      )}

      {state === 'failed' && (
        /*
         * Showing the address is the thing this panel otherwise never does. It
         * is the lesser harm: a copy button that quietly does nothing leaves her
         * with no way to use the feature at all, and she is looking at her own
         * link on her own screen.
         */
        <div className="mt-2.5">
          <p role="alert" className="text-[12.5px] text-crit">
            Your browser would not let us copy that. Select the address and copy it yourself.
          </p>

          <input
            readOnly
            value={link.url}
            aria-label={`Calendar address for ${link.name}`}
            onFocus={(e) => e.currentTarget.select()}
            className="mt-1.5 w-full rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-[12.5px]"
          />
        </div>
      )}
    </div>
  )
}
